/**
 * Tooltip-teksten voor het handmatig-teken in de matrix. Pure functies, geen React, geen JSX.
 *
 * Stond eerder in Weekrooster.jsx. Hier weggehaald om dezelfde reden als fteLabels.js: de
 * diagnostiek (client/test/diag/handmatig-teken-inventaris.mjs) moet de LETTERLIJKE zin kunnen
 * tonen die op het scherm komt, en node kan geen .jsx importeren. Het alternatief — het
 * diagnosescript een eigen kopie van de zinnen geven — is precies de fout die daar al een keer
 * gemaakt is: een diagnose die iets anders meet dan wat er draait.
 *
 * De teruggegeven treffer komt uit handmatigOpCel (lib/handmatigmarkering.js); dit bestand
 * doet alleen de bewoording.
 */

/**
 * "Arnhem — Late dienst" uit een cel-adres ({ gemeenteKey, dienstId }).
 *
 * Zelfde omzetting en zelfde terugval als plaatsTekst in WijzigingViewer.jsx: bestaat de
 * gemeente of de dienst niet meer (hernoemd of verwijderd na het loggen), dan blijft de kale
 * sleutel staan — een oud record mag nooit leeg of misleidend worden. BEWUST NIET de
 * hardgecodeerde GEMEENTE_LABELS / DIENST_LABELS uit ConflictBanner.jsx: die kennen alleen de
 * drie Arnhemse gemeenten en zouden bij Maastricht het verkeerde of niets tonen.
 *
 * De labels komen dus uit de DATA. Dat ze niet consistent geschreven zijn ("dagdienst" bij
 * Arnhem naast "Dagdienst" bij Lingewaard) is een kwestie voor Beheer > Gemeenten, niet iets
 * om hier glad te strijken: elke correctie in code zou de instelling van de gebruiker
 * overschrijven.
 */
export function slotLabel(slot, regels) {
  if (!slot) return '';
  const gem = regels?.gemeenten?.[slot.gemeenteKey];
  const gemNaam = gem?.naam || slot.gemeenteKey || '?';
  const dienstNaam = gem?.diensten?.[slot.dienstId]?.label || slot.dienstId;
  return dienstNaam ? `${gemNaam} — ${dienstNaam}` : gemNaam;
}

/**
 * Tijdstip van de ingreep, kort en in de tijdzone van de kijker.
 *
 * Het tijdstip is een INSTANT (server-gestempeld, server/src/index.js:1059), dus
 * toLocaleString is hier het juiste antwoord — zelfde vorm als formatTijd in
 * WijzigingViewer.jsx en in LogViewer.jsx. Bewust GEEN toISOString: dat rekent naar UTC
 * en schuift rond middernacht een dag terug, en die fout is in dit project al vier keer
 * gemaakt (lib/tijd.js:4-8).
 */
export function handmatigTijd(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/**
 * De tooltipzin bij een treffer van handmatigOpCel. Drie takken, alle drie met de dienst erbij.
 *
 * Het VOORZETSEL verschilt per tak, omdat de dienst een andere rol speelt:
 *   toevoegen  → "op ..."   : daar staat hij nu
 *   weghalen   → "uit ..."  : daar kwam hij vandaan, hij staat er niet meer
 *   verplaatst → "van ... naar ..."
 * "bij" voor allebei las scheef bij weghalen ("weggehaald bij Arnhem — Late dienst" klinkt
 * alsof hij daar nog staat), vandaar per tak een eigen voorzetsel.
 *
 * Eerder noemde alleen de verplaats-tak de dienst; bij weghalen stond er enkel "Handmatig
 * weggehaald door ...", en dan is niet te zien WAT er weg is — precies de vraag die je bij een
 * lege cel stelt. Het adres zat al in de treffer (gemeenteKey/dienstId), dus het hoefde alleen
 * nog gezegd te worden.
 */
export function handmatigTitel(handmatig, regels) {
  if (!handmatig) return null;
  const wanneer = handmatigTijd(handmatig.tijdstip);
  const staart = `door ${handmatig.doorNaam}${wanneer ? ` — ${wanneer}` : ''}.`;

  // Een verplaatsing is ÉÉN handeling die twee slots raakt (celmenu, Weekrooster.jsx:259-260).
  // Beide diensten noemen zegt precies wat er gebeurd is; de oude tekst maakte er "meer dan
  // één dienst aangepast" van, wat las alsof er twee keer was ingegrepen.
  if (handmatig.operatie === 'verplaatst') {
    return `Handmatig verplaatst van ${slotLabel(handmatig.van, regels)}`
      + ` naar ${slotLabel(handmatig.naar, regels)}, ${staart}`;
  }

  const plek = slotLabel({ gemeenteKey: handmatig.gemeenteKey, dienstId: handmatig.dienstId }, regels);
  const basis = handmatig.operatie === 'toevoegen'
    ? `Handmatig neergezet op ${plek}, ${staart}`
    : `Handmatig weggehaald uit ${plek}, ${staart}`;

  // Pas vanaf DRIE slots is er echt meer dan één handeling; twee slots zijn hierboven al als
  // verplaatsing afgevangen (of het zijn er twee dezelfde kant op, en dan klopt de zin ook).
  // De zin noemt de plek van het JONGSTE slot, dus de verwijzing naar het volledige logboek
  // blijft nodig.
  return handmatig.aantal > 1
    ? `${basis}\n\nOp deze dag is meer dan één dienst met de hand aangepast — zie Beheer > Wijzigingen.`
    : basis;
}
